import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ReceiptFields } from "@/components/ReceiptReviewCard";
import { Receipt, Pencil, Trash2, Eye, Loader2, Check, X } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ r, categories, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ vendor: r.vendor, date: r.date || "", amount: r.amount, category: r.category, items: r.items });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.date) { toast.error("Add the receipt date"); return; }
    setBusy(true);
    try {
      await api.put(`/vault/receipts/${r.receipt_id}`, { ...form, amount: parseFloat(form.amount || 0) });
      setEditing(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this receipt?")) return;
    setBusy(true);
    try { await api.delete(`/vault/receipts/${r.receipt_id}`); onChanged(); }
    catch { toast.error("Could not delete"); } finally { setBusy(false); }
  };
  const view = async () => {
    try {
      const { data } = await api.get(`/files/${r.storage_path}`, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error("Could not open receipt"); }
  };

  if (editing) {
    return (
      <li className="bg-[#F9F9F8] border border-[#E5E5E5] rounded-2xl p-4" data-testid={`receipt-edit-${r.receipt_id}`}>
        <ReceiptFields form={form} setForm={setForm} categories={categories} idPrefix={`edit-${r.receipt_id}`} />
        <div className="mt-3 flex gap-2">
          <Button onClick={save} disabled={busy} size="sm" data-testid={`receipt-edit-save-${r.receipt_id}`} className="rounded-full bg-[#114A31] text-white hover:bg-[#1A6B46]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Save</>}
          </Button>
          <Button onClick={() => setEditing(false)} size="sm" variant="ghost" className="rounded-full"><X className="h-4 w-4 mr-1" /> Cancel</Button>
        </div>
      </li>
    );
  }
  return (
    <li className="flex items-center gap-3 bg-white border border-[#E5E5E5] rounded-2xl px-4 py-3" data-testid={`receipt-row-${r.receipt_id}`}>
      <div className="h-9 w-9 rounded-xl bg-[#E6F0EB] flex items-center justify-center shrink-0"><Receipt className="h-4 w-4 text-[#114A31]" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1C1C1C] truncate">{r.vendor || "Unknown vendor"}
          {r.status === "draft" && <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92600A]">Needs review</span>}
        </p>
        <p className="text-xs text-[#595D5A] truncate">{r.date || "No date"} · {r.category}{r.source === "bank" ? " · from bank import" : r.source === "email" ? " · emailed in" : ""}{r.items ? ` · ${r.items}` : ""}</p>
      </div>
      <span className="font-heading font-semibold text-[#1C1C1C] shrink-0" data-testid={`receipt-amount-${r.receipt_id}`}>{money(r.amount)}</span>
      <div className="flex gap-1 shrink-0">
        {r.storage_path && <button onClick={view} className="p-1.5 text-[#595D5A] hover:text-[#114A31]" title="View" data-testid={`receipt-view-${r.receipt_id}`}><Eye className="h-4 w-4" /></button>}
        <button onClick={() => setEditing(true)} className="p-1.5 text-[#595D5A] hover:text-[#114A31]" title="Edit" data-testid={`receipt-edit-btn-${r.receipt_id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={del} disabled={busy} className="p-1.5 text-[#595D5A] hover:text-[#991B1B]" title="Delete" data-testid={`receipt-delete-${r.receipt_id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
};

export const ReceiptList = ({ receipts, categories, onChanged }) => {
  if (!receipts.length) {
    return (
      <div className="bg-white border border-dashed border-[#E5E5E5] rounded-2xl p-8 text-center" data-testid="vault-empty">
        <Receipt className="h-8 w-8 text-[#595D5A] mx-auto" />
        <p className="mt-2 text-sm font-medium text-[#1C1C1C]">No receipts yet</p>
        <p className="text-xs text-[#595D5A]">Snap your first receipt above — we'll read the vendor, date and total for you.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="vault-receipt-list">
      {receipts.map((r) => <Row key={r.receipt_id} r={r} categories={categories} onChanged={onChanged} />)}
    </ul>
  );
};

export default ReceiptList;
